import { Button, Typography, Box, styled } from '@quillbot/design-system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Fade } from '@mui/material';

interface TabDataProps {
  imgUrl: string;
  description: string;
}
export interface IProps {
  text: string;
}

export const StyledButton = styled(Button)`
  && {
    border-radius: 20px;
    font-size: 17px;
    font-weight: 600;
    padding-right: 24px;
    padding-left: 24px;
  }
`;

export const StyledParaTypo = styled(Typography)`
  && {
    color: ${(props) => props.theme.palette.text.primary};
    font-size: 16px;
    font-weight: 400;
  }
`;

export const StyledTitleTypo = styled(Typography)`
  && {
    font-family: 'Open Sans';
    color: ${(props) => props.theme.palette.text.primary};
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }
`;

export const StyledQuotesTypo = styled(Typography)`
  && {
    font-family: 'Open Sans';
    color: ${(props) => props.theme.palette.text.primary};
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    font-style: italic;
    text-align: center;
    margin: 0 auto;
    max-width: 573px;
  }
`;

export const StyledContainer = styled(Box)`
  max-width: 665px;
  margin: 0 auto;
`;

export const StyledCheckBox = (props: IProps) => {
  const { text } = props;
  return (
    <Box
      pl="12px"
      pr="4px"
      py="20px"
      bgcolor="rgba(217, 217, 217, 0.24)"
      display="flex"
      alignItems="center"
      flexBasis={{ md: 'calc(33% - 12px)', xs: 'calc(100% - 12px)' }}
      m="6px"
      borderRadius="9px"
    >
      <CheckCircleIcon sx={{ color: '#4CBD75' }} />
      <Box pr="6px" />
      <StyledParaTypo>{text}</StyledParaTypo>
    </Box>
  );
};

export const StyledSteps = (props: {
  step: string;
  title: string;
  description: string;
  bgColor: string;
  isMobile?: boolean;
}) => {
  const { step, title, description, bgColor, isMobile } = props;
  return (
    <Box
      boxShadow={isMobile ? 0 : 3}
      height={isMobile ? 276 : 164}
      border={isMobile ? '1px solid' : 'none'}
      borderColor="grey.700"
      overflow="hidden"
      display="flex"
      borderRadius={isMobile ? ' 20px' : '20px 8px 8px 20px'}
    >
      <Box display="flex" height={isMobile ? 100 : '100%'} width={isMobile ? 116 : 204}>
        <Box pl="12px" pt="16px" flex={1} textAlign="left" bgcolor={bgColor}>
          <Typography
            pl="4px"
            fontSize={isMobile ? '12px' : '24px'}
            fontWeight={600}
            color="grey.50"
          >
            STEP
          </Typography>
          <Typography
            fontSize={isMobile ? '46px' : '96px'}
            lineHeight={isMobile ? '46px' : '96px'}
            color="grey.50"
          >
            {step}
          </Typography>
        </Box>
        <svg
          viewBox="0 0 12 24"
          style={{ marginLeft: '-1px' }}
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m0 0 12 12L0 24z" fill={bgColor} fillRule="evenodd" />
        </svg>
      </Box>
      <Box pr={isMobile ? '15px' : '24px'} />
      <Box flex={1} py="20px" pr={isMobile ? '16px' : '32px'}>
        <StyledTitleTypo
          lineHeight={isMobile ? '20px !important' : '32px !important'}
          fontSize={isMobile ? '20px !important' : '24px'}
          fontWeight={700}
        >
          {title}
        </StyledTitleTypo>
        <Box pt="8px" />
        <Typography
          dangerouslySetInnerHTML={{ __html: description }}
          color="text.primary"
          lineHeight="19px"
          fontSize="14px"
        />
      </Box>
    </Box>
  );
};

export function StyledTabPanel({ data, ...otherProps }: { data: TabDataProps }) {
  const { imgUrl, description } = data;
  return (
    <Fade {...otherProps} timeout={700} in appear easing="linear">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
        px={{ md: '75px', xs: '12px' }}
        maxWidth={1457}
        margin="0 auto"
      >
        <Box flex={1} height={{ md: 408, xs: 233 }} width={{ md: 612, xs: '100%' }}>
          <img
            height="100%"
            width="100%"
            src={imgUrl}
            style={{ borderRadius: '20px', objectFit: 'cover', objectPosition: 'center' }}
            alt="tab-panel"
          />
        </Box>
        <Box pr={{ md: '64px', xs: 0 }} />
        <StyledParaTypo
          flex={1}
          px={{ md: 0, xs: '30px' }}
          pt={{ md: 0, xs: '28px' }}
          sx={{ flex: 1 }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </Box>
    </Fade>
  );
}
